import type { AdminCustomer } from "@/mocks/admin";
import type { Order } from "@/mocks/orders";

interface CustomerDetailModalProps {
  customer: AdminCustomer;
  orders: Order[];
  onClose: () => void;
}

const statusColor = (status: string) => {
  switch (status) {
    case "delivered": return "bg-accent-100 text-accent-800";
    case "shipped": return "bg-secondary-100 text-secondary-800";
    case "processing": return "bg-primary-100 text-primary-800";
    case "cancelled": return "bg-foreground-100 text-foreground-600";
    default: return "bg-background-200 text-foreground-500";
  }
};

export default function CustomerDetailModal({ customer, orders, onClose }: CustomerDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl border border-background-200/70">
        {/* Header */}
        <div className="px-6 py-4 border-b border-background-200/70 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary-200 overflow-hidden flex-shrink-0">
              <img src={customer.avatarUrl} alt={customer.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-bold text-foreground-950">{customer.name}</h3>
              <p className="text-sm text-foreground-500">Customer since {customer.joinDate}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-background-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Contact Info</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-sm text-foreground-400"></i>
                  <span className="text-sm text-foreground-700">{customer.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-sm text-foreground-400"></i>
                  <span className="text-sm text-foreground-700">{customer.phone}</span>
                </div>
              </div>
            </div>
            <div className="bg-background-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Customer Stats</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-lg font-bold text-foreground-950 font-heading">{customer.totalOrders}</p>
                  <p className="text-xs text-foreground-500">Total Orders</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground-950 font-heading">${customer.totalSpent.toFixed(2)}</p>
                  <p className="text-xs text-foreground-500">Total Spent</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order history */}
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Order History</p>
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70">
                      {["Order #", "Date", "Items", "Total", "Status"].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-b border-background-100">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">{order.orderNumber}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground-500 whitespace-nowrap">{order.date}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">{order.items.length} item{order.items.length !== 1 ? "s" : ""}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">${order.total.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${statusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {orders.length === 0 && (
                <div className="py-12 text-center">
                  <i className="ri-inbox-line text-2xl text-foreground-300 mb-2 block"></i>
                  <p className="text-sm text-foreground-500">No orders yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}